import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login/Login';
import Register from './pages/Register/Register';
import UserView from './components/UserView/UserView';
import SettingUsers from './components/SettingUsers/SettingUsers';
import Home from "./screens/Home";
import EquipmentConsumption from "./components/EquipmentConsumption/EquipmentConsumption";
import Cities from './components/Cities/Cities';
import SettingCities from './components/SettingCities/SettingCities';
import States from './components/States/States';
import SettingStates from './components/SettingStates/SettingStates';
import Battery from './components/Battery/Battery';
import SettingBaterry from './components/SettingBaterry/SettingBaterry';
import ControllerEnergy from './components/ControllerEnergy/ControllerEnergy';
import SettingController from './components/SettingController/SettingController';
import Investor from './components/Investor/Investor';
import SettingInvestor from './components/SettingInvestor/SettingInvestor';
import AdminView from './components/AdminView/AdminView';
import SolarRadiationGraph from './components/SolarRadiationGraph/SolarRadiationGraph';
import NotFound from "./pages/NotFound/NotFound";
import firebaseApp from "./firebase/credenciales";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFirestore, doc, getDoc, enableIndexedDbPersistence } from "firebase/firestore";

const auth = getAuth(firebaseApp);
const firestore = getFirestore(firebaseApp);

enableIndexedDbPersistence(firestore)
  .catch((err) => {
    if (err.code === 'failed-precondition') {
      console.log('Multiple tabs open, persistence may not work');
    } else if (err.code === 'unimplemented') {
      console.log('The current browser does not support all of the features required for persistence');
    }
  });

function App() {
  const [user, setUser] = useState(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [connectionType, setConnectionType] = useState(null);
  const [loadingUserInfo, setLoadingUserInfo] = useState(true);

  const getRol = async (uid) => {
    const docRef = doc(firestore, `usuarios/${uid}`);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? docSnap.data().rol : null;
  };

  const getUserInfo = async (usuarioFirebase) => {
    const rol = await getRol(usuarioFirebase.uid);
    const userData = {
      uid: usuarioFirebase.uid,
      email: usuarioFirebase.email,
      rol: rol,
    };
    setUser(userData);
    setLoadingUserInfo(false);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (usuarioFirebase) => {
      if (usuarioFirebase) {
        getUserInfo(usuarioFirebase);
      } else {
        setUser(null);
        setLoadingUserInfo(false);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      checkConnectionType();
    };

    const handleOffline = () => {
      setIsOffline(true);
    };

    const checkConnectionType = () => {
      if (navigator.connection) {
        const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        setConnectionType(connection.effectiveType);
      } else {
        setConnectionType(null);
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    checkConnectionType();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  function ProtectedRoute({ children }) {
    return user ? children : <Navigate to="/login" />;
  }

  function PublicRoute({ children }) {
    return !user ? children : <Navigate to="/home" />;
  }

  function AdminProtectedRoute({ children }) {
    return user?.rol === "admin" ? children : <Navigate to="/admin" />;
  }

  function UserProtectedRoute({ children }) {
    return user?.rol === "user" ? children : <Navigate to="/user" />;
  }

  if (loadingUserInfo) {
    return console.log("Cargando información del usuario...");
  }

  return (
    <Router>
      <Routes>
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <ProtectedRoute>
              <Register />
            </ProtectedRoute>
          }
        />
        <Route
          path="/user"
          element={
            <UserProtectedRoute>
              <UserView />
            </UserProtectedRoute>
          }
        />
        <Route
          path="/admin/users/settings"
          element={
            <ProtectedRoute>
              <SettingUsers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/solar-radiation"
          element={
            <ProtectedRoute>
              <SolarRadiationGraph />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/ciudades"
          element={
            <AdminProtectedRoute>
              <Cities />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/ciudades/settings"
          element={
            <AdminProtectedRoute>
              <SettingCities />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/estados"
          element={
            <AdminProtectedRoute>
              <States />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/estados/settings"
          element={
            <AdminProtectedRoute>
              <SettingStates />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/baterias"
          element={
            <AdminProtectedRoute>
              <Battery />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/baterias/settings"
          element={
            <AdminProtectedRoute>
              <SettingBaterry />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/controladores"
          element={
            <AdminProtectedRoute>
              <ControllerEnergy />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/controladores/settings"
          element={
            <AdminProtectedRoute>
              <SettingController />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/inversores"
          element={
            <AdminProtectedRoute>
              <Investor />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/inversores/settings"
          element={
            <AdminProtectedRoute>
              <SettingInvestor />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <AdminProtectedRoute>
              <AdminView />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/home"
          element={
            <ProtectedRoute>
              <Home user={user} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/equipment-consumption"
          element={
            <ProtectedRoute>
              <EquipmentConsumption />
            </ProtectedRoute>
          }
        />
        <Route path="/" element={user ? <Navigate to="/home" /> : <Navigate to="/login" />} />
        <Route path="*" element={<NotFound user={user} />} />
      </Routes>

      {isOffline && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', backgroundColor: 'red', color: 'white', textAlign: 'center', padding: '10px' }}>
          ¡Sin Acceso a Internet! Por favor, revisa tu conexión a Internet.
        </div>
      )}
    </Router>
  );
}

export default App;
